import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Building2, FileText, Users, DollarSign, TrendingUp, Home } from 'lucide-react';
import UserMenu from './UserMenu';
import MobileMenu from './MobileMenu';
import NotificationBell from './NotificationBell';

/**
 * ResponsiveNavbar Component
 * Adaptive navigation that shows horizontal menu on desktop
 * and hamburger menu on mobile/tablet
 */
export default function ResponsiveNavbar({ isAdmin = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  
  // Navigation items based on role
  const investorNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/assets', label: 'Assets', icon: Building2 },
  ];
  
  const adminNavItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/assets', label: 'Assets', icon: Building2},
    { path: '/admin/investors', label: 'Investors', icon: Users },
    { path: '/admin/commitments', label: 'Commitments', icon: FileText },
    { path: '/admin/payouts', label: 'Payouts', icon: DollarSign },
  ];
  
  const navItems = isAdmin ? adminNavItems : investorNavItems;
  
  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false); // Close menu after navigation
  };
  
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  
  return (
    <>
      <header className="border-b border-white/10 backdrop-blur-md sticky top-0 z-50">
        <div className="container-responsive max-w-7xl mx-auto">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <button
              onClick={() => handleNavigate(isAdmin ? '/admin' : '/dashboard')}
              className="flex items-center gap-2 sm:gap-3 flex-shrink-0"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#D4AF37] to-[#F5C542] rounded-lg flex items-center justify-center text-[#0A1F44] font-bold text-xs sm:text-sm hover:scale-110 transition-transform">
                IV
              </div>
              <span className="font-['Outfit'] text-lg sm:text-xl font-bold">
                InvestVault{isAdmin && <span className="text-sm ml-2 text-[#D4AF37]">Admin</span>}
              </span>
            </button>
            
            {/* Desktop Navigation - Hidden on mobile/tablet */}
            <nav className="hidden lg:flex items-center gap-6">
              {navItems.map(({ path, label, icon: Icon }) => (
                <button
                  key={path}
                  onClick={() => handleNavigate(path)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg transition-all font-medium
                    ${isActive(path)
                      ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                      : 'text-[#94A3B8] hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
            
            {/* Right Side - Notifications + Avatar + Hamburger */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Notifications */}
              <NotificationBell />
              
              {/* User Avatar  */}
              <div className="relative" style={{ overflow: 'visible' }}>
                <UserMenu />
              </div>
              
              {/* Hamburger Button - Visible on mobile/tablet only */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white/80 hover:text-white transition-colors touch-target"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu Drawer */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={closeMobileMenu}>
        {/* Navigation Links */}
        <nav className="flex flex-col p-4 space-y-2">
          {navItems.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              onClick={() => handleNavigate(path)}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left touch-target
                ${isActive(path)
                  ? 'text-[#D4AF37] bg-[#D4AF37]/10 font-medium'
                  : 'text-white/80 hover:text-white hover:bg-white/5'
                }
              `}
            >
              <Icon className="w-5 h-5" />
              <span className="text-base">{label}</span>
            </button>
          ))}
        </nav>
        
        {/* Mobile Menu Footer */}
        <div className="mt-auto p-4 border-t border-white/10">
          <button
            onClick={() => handleNavigate('/')}
            className="flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white transition-colors w-full text-left touch-target"
          >
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </MobileMenu>
    </>
  );
}
